import React from 'react';
import { X } from 'lucide-react';
import { computeAreaSqm } from './signageSqmUtils';

const SHAPES = ['Rectangular', 'Circular', 'Triangular', 'Hazard', 'Octagonal', 'Gantry'];
const BOARD_DESCRIPTIONS = [
  'Cautionary Sign', 'Mandatory Sign', 'Informatory Sign', 'Warning Sign',
  'Guide Sign', 'Gantry Board', 'Overhead Sign', 'Chevron Marker',
  'Kilometre Stone', 'Direction Sign', 'Toll Plaza Sign', 'Speed Limit Sign',
  'Highway Marker', 'Delineator', 'Other',
];
const CONCRETE_GRADES = [
  { name: 'M15', rate: 4500 },
  { name: 'M20', rate: 5500 },
  { name: 'M25', rate: 6500 },
];
const DEFAULT_REBAR_RATE = 75;
const needsLH = (shape) => ['Rectangular', 'Gantry', 'Hazard'].includes(shape);

const SignageBoardItem = ({
  item, idx, form, setForm, calcResult, setNeedsRecalc,
  updateItem, removeItem, rateCard, getRateFromCard,
  laminates, showFoundation, showInternalCosts, netAmount,
}) => {
  const itemCalc = calcResult?.items?.[idx];

  return (
    <div data-testid={`signage-item-${idx}`} className="border border-gray-100 rounded-lg p-3 bg-gray-50/50 space-y-2">
      {/* Row 0: Board Description */}
      <div className="flex items-center gap-2">
        <div className="flex-1">
          <label className="block text-[10px] text-gray-500 mb-0.5">Board Description</label>
          <div className="flex gap-2">
            <select data-testid={`board-desc-select-${idx}`}
              value={BOARD_DESCRIPTIONS.includes(item.description) ? item.description : (item.description ? 'Other' : '')}
              onChange={e => { if (e.target.value === 'Other') updateItem(idx, 'description', ''); else updateItem(idx, 'description', e.target.value); }}
              className="w-48 px-2 py-1.5 text-xs border border-gray-200 rounded-md">
              <option value="">Select Type</option>
              {BOARD_DESCRIPTIONS.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
            {(!BOARD_DESCRIPTIONS.includes(item.description) || item.description === '') && (
              <input data-testid={`board-desc-input-${idx}`} type="text" value={item.description} onChange={e => updateItem(idx, 'description', e.target.value)} placeholder="e.g. Cautionary Sign W1" className="flex-1 px-2 py-1.5 text-xs border border-gray-200 rounded-md" />
            )}
          </div>
        </div>
        {form.items.length > 1 && <button onClick={() => removeItem(idx)} className="p-1 rounded hover:bg-red-50 text-red-400 hover:text-red-600 mt-4"><X className="w-3.5 h-3.5" /></button>}
      </div>

      {/* Row 1: Shape + Dimensions + Qty */}
      <div className="grid grid-cols-12 gap-2 items-end">
        <div className="col-span-2">
          <label className="block text-[10px] text-gray-500 mb-0.5">Shape</label>
          <select data-testid={`signage-item-shape-${idx}`} value={item.shape} onChange={e => updateItem(idx, 'shape', e.target.value)} className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-md">
            {SHAPES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        {needsLH(item.shape) ? (
          <>
            <div className="col-span-2">
              <label className="block text-[10px] text-gray-500 mb-0.5">Length (mm)</label>
              <input data-testid={`signage-item-length-${idx}`} type="number" value={item.length_mm} onChange={e => updateItem(idx, 'length_mm', e.target.value)} placeholder="e.g. 3000" className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-md" />
            </div>
            <div className="col-span-2">
              <label className="block text-[10px] text-gray-500 mb-0.5">Height (mm)</label>
              <input data-testid={`signage-item-height-${idx}`} type="number" value={item.height_mm} onChange={e => updateItem(idx, 'height_mm', e.target.value)} placeholder="e.g. 1200" className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-md" />
            </div>
          </>
        ) : (
          <div className="col-span-4">
            <label className="block text-[10px] text-gray-500 mb-0.5">{item.shape === 'Circular' ? 'Diameter' : 'Side'} (mm)</label>
            <input data-testid={`signage-item-diameter-${idx}`} type="number" value={item.diameter_mm} onChange={e => updateItem(idx, 'diameter_mm', e.target.value)} placeholder="e.g. 900" className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-md" />
          </div>
        )}
        <div className="col-span-2">
          <label className="block text-[10px] text-gray-500 mb-0.5">Qty</label>
          <input data-testid={`signage-item-qty-${idx}`} type="number" value={item.qty} onChange={e => updateItem(idx, 'qty', e.target.value)} min="1" className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-md" />
        </div>
        <div className="col-span-2">
          <label className="block text-[10px] text-gray-500 mb-0.5">Retro Sheet</label>
          <select value={item.retro_sheet} onChange={e => updateItem(idx, 'retro_sheet', e.target.value)} className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-md">
            <option value="">Select</option>
            {(rateCard?.retro_sheets || []).map(r => <option key={r.name} value={r.name}>{r.name}</option>)}
          </select>
        </div>
        <div className="col-span-2">
          <label className="block text-[10px] text-gray-500 mb-0.5">Type of Work <span className="text-[9px] text-gray-400 ml-0.5">(override)</span></label>
          <select data-testid={`signage-item-work-type-${idx}`} value={item.work_type || ''} onChange={e => updateItem(idx, 'work_type', e.target.value)} className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-md">
            <option value="">Default ({form.work_type || '—'})</option>
            {(rateCard?.work_types || []).map(w => <option key={w.name} value={w.name}>{w.name}</option>)}
          </select>
        </div>
      </div>

      {/* Row 2: Materials with Rate Overrides */}
      <div className="grid grid-cols-12 gap-2 items-end">
        <div className="col-span-2">
          <label className="block text-[10px] text-gray-500 mb-0.5">Base Material</label>
          <select value={item.base_material} onChange={e => updateItem(idx, 'base_material', e.target.value)} className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-md">
            <option value="">Select</option>
            {(rateCard?.base_materials || []).map(m => <option key={m.name} value={m.name}>{m.name}</option>)}
          </select>
        </div>
        <div className="col-span-1">
          <label className="block text-[9px] text-amber-600 mb-0.5">₹/sqft</label>
          <input data-testid={`base-rate-${idx}`} type="number" value={item.base_material_rate} onChange={e => updateItem(idx, 'base_material_rate', e.target.value)} placeholder={getRateFromCard('base', item.base_material) || '0'} className="w-full px-2 py-1.5 text-xs border border-amber-200 rounded-md bg-amber-50/30" />
        </div>
        <div className="col-span-1">
          <label className="block text-[9px] text-amber-600 mb-0.5">Retro ₹</label>
          <input data-testid={`retro-rate-${idx}`} type="number" value={item.retro_sheet_rate} onChange={e => updateItem(idx, 'retro_sheet_rate', e.target.value)} placeholder={getRateFromCard('retro', item.retro_sheet) || '0'} className="w-full px-2 py-1.5 text-xs border border-amber-200 rounded-md bg-amber-50/30" />
        </div>
        <div className="col-span-1">
          <label className="block text-[9px] text-amber-600 mb-0.5">Work ₹</label>
          <input data-testid={`work-rate-${idx}`} type="number" value={item.work_type_rate} onChange={e => updateItem(idx, 'work_type_rate', e.target.value)} placeholder={getRateFromCard('work', item.work_type || form.work_type) || '0'} className="w-full px-2 py-1.5 text-xs border border-amber-200 rounded-md bg-amber-50/30" />
        </div>
        <div className="col-span-1">
          <label className="block text-[9px] text-amber-600 mb-0.5">Labour ₹</label>
          <input data-testid={`labour-rate-${idx}`} type="number" value={item.labour_rate} onChange={e => updateItem(idx, 'labour_rate', e.target.value)} placeholder={rateCard?.labour_rate || '25'} className="w-full px-2 py-1.5 text-xs border border-amber-200 rounded-md bg-amber-50/30" />
        </div>
        <div className="col-span-2">
          <label className="block text-[10px] text-gray-500 mb-0.5">Post Type (optional)</label>
          <select value={item.post_type} onChange={e => updateItem(idx, 'post_type', e.target.value)} className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-md">
            <option value="">None</option>
            {(rateCard?.post_types || []).map(p => <option key={p.name} value={p.name}>{p.name}</option>)}
          </select>
        </div>
        {item.post_type && (
          <>
            <div className="col-span-2">
              <label className="block text-[10px] text-gray-500 mb-0.5">Post Length (ft)</label>
              <input type="number" value={item.post_length_ft} onChange={e => updateItem(idx, 'post_length_ft', e.target.value)} placeholder="20" className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-md" />
            </div>
            <div className="col-span-2">
              <label className="block text-[10px] text-gray-500 mb-0.5">No. of Posts</label>
              <input type="number" value={item.num_posts} onChange={e => updateItem(idx, 'num_posts', e.target.value)} placeholder="2" className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-md" />
            </div>
            <div className="col-span-3">
              <label className="block text-[10px] text-amber-600 mb-0.5">Post Weight (kg) <span className="text-[9px] text-amber-400">manual override</span></label>
              <input data-testid={`post-weight-${idx}`} type="number" value={item.post_weight_override} onChange={e => updateItem(idx, 'post_weight_override', e.target.value)}
                placeholder={(() => { const w20 = (rateCard?.post_types || []).find(p => p.name === item.post_type)?.weight_per_20ft || 0; return w20 > 0 && item.post_length_ft && item.num_posts ? `~${((Number(item.post_length_ft)/20)*w20*Number(item.num_posts)).toFixed(1)} kg (formula)` : 'Enter weight'; })()}
                className="w-full px-2 py-1.5 text-xs border border-amber-200 rounded-md bg-amber-50/30 placeholder:text-amber-400" />
            </div>
          </>
        )}
      </div>

      {/* Row 2b: Frame + Support Length */}
      <div className="grid grid-cols-12 gap-2 items-end">
        <div className="col-span-3">
          <label className="block text-[10px] text-gray-500 mb-0.5">Frame Type (optional)</label>
          <select value={item.frame_type} onChange={e => updateItem(idx, 'frame_type', e.target.value)} className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-md">
            <option value="">None</option>
            {(rateCard?.frame_types || []).map(f => <option key={f.name} value={f.name}>{f.name}</option>)}
          </select>
        </div>
        {item.frame_type && (
          <>
            <div className="col-span-3">
              <label className="block text-[10px] text-gray-500 mb-0.5">Support Length (ft) <span className="text-[9px] text-gray-400">optional, adds to perimeter</span></label>
              <input data-testid={`frame-support-${idx}`} type="number" value={item.frame_support_length_ft} onChange={e => updateItem(idx, 'frame_support_length_ft', e.target.value)} placeholder="0" className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-md" />
            </div>
            <div className="col-span-3">
              <label className="block text-[10px] text-amber-600 mb-0.5">Frame Weight (kg) <span className="text-[9px] text-amber-400">manual override</span></label>
              <input data-testid={`frame-weight-${idx}`} type="number" value={item.frame_weight_override} onChange={e => updateItem(idx, 'frame_weight_override', e.target.value)} placeholder="Enter weight" className="w-full px-2 py-1.5 text-xs border border-amber-200 rounded-md bg-amber-50/30 placeholder:text-amber-400" />
            </div>
          </>
        )}
      </div>

      {/* Row 3: Laminate Selection */}
      {((item.work_type || form.work_type) === 'Print & Lamination' && laminates.length > 0) && (
        <div className="grid grid-cols-12 gap-2 items-end">
          <div className="col-span-5">
            <label className="block text-[10px] text-gray-500 mb-0.5">Laminate Film <span className="text-[9px] text-blue-500 ml-1">(cost pulled from Product Master)</span></label>
            <select data-testid={`signage-item-laminate-${idx}`} value={item.laminate_sku} onChange={e => updateItem(idx, 'laminate_sku', e.target.value)} className="w-full px-2 py-1.5 text-xs border border-blue-200 rounded-md bg-blue-50/30 focus:ring-1 focus:ring-blue-300">
              <option value="">No Laminate Selected</option>
              {laminates.map(l => <option key={l.sku} value={l.sku}>{l.name} {l.cost_price > 0 ? `(₹${l.cost_price}/sqft)` : '(rate not set)'}</option>)}
            </select>
          </div>
          {item.laminate_sku && (() => {
            const sel = laminates.find(l => l.sku === item.laminate_sku);
            return sel ? <div className="col-span-4 flex items-end gap-2 pb-0.5"><span className="text-[10px] bg-indigo-50 text-indigo-700 px-2 py-1 rounded-md font-medium">{sel.cost_price > 0 ? `₹${sel.cost_price}/sqft` : 'Rate not set — update in Product Master'}</span></div> : null;
          })()}
        </div>
      )}

      {/* Row 4: Per-item rate override */}
      <div className="grid grid-cols-12 gap-2 items-end">
        <div className="col-span-3">
          <label className="block text-[10px] text-gray-500 mb-0.5">Rate Override (INR/sqft) <span className="text-[9px] text-amber-500 ml-1">optional</span></label>
          <input data-testid={`signage-item-override-${idx}`} type="number" value={item.sqft_rate_override}
            onChange={e => { updateItem(idx, 'sqft_rate_override', e.target.value); setNeedsRecalc(true); }}
            placeholder={itemCalc ? `Derived: ₹${itemCalc.cost_per_sqft}` : 'Auto from rate card'}
            className="w-full px-2 py-1.5 text-xs border border-amber-200 rounded-md bg-amber-50/20 focus:ring-1 focus:ring-amber-300" />
        </div>
        {itemCalc?.is_override && <div className="col-span-4 flex items-end pb-0.5"><span className="text-[9px] bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full font-medium">Using custom rate: ₹{itemCalc.cost_per_sqft}/sqft</span></div>}
        <div className="col-span-5">
          <label className="block text-[10px] text-gray-500 mb-0.5">Specific Description <span className="text-[9px] text-gray-400 ml-1">shown in PDF</span></label>
          <input data-testid={`signage-item-specific-desc-${idx}`} type="text" value={item.specific_description || ''}
            onChange={e => updateItem(idx, 'specific_description', e.target.value)}
            placeholder="e.g. As per IRC:67-2012, Type W1, 600x600mm"
            className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-md" />
        </div>
      </div>

      {/* Foundation Section */}
      {showFoundation && (
        <div className="border border-teal-200 rounded-lg p-3 bg-teal-50/30 space-y-2">
          <label className="flex items-center gap-2 cursor-pointer">
            <input data-testid={`foundation-toggle-${idx}`} type="checkbox" checked={item.foundation_enabled || false} onChange={e => updateItem(idx, 'foundation_enabled', e.target.checked)} className="w-3.5 h-3.5 rounded border-teal-300 text-teal-600 focus:ring-teal-200" />
            <span className="text-[11px] font-semibold text-teal-800">Foundation / Pit Work</span>
          </label>
          {item.foundation_enabled && (
            <>
              <div className="grid grid-cols-12 gap-2 items-end">
                <div className="col-span-2">
                  <label className="block text-[9px] font-medium text-teal-700 mb-0.5">Concrete Grade</label>
                  <select data-testid={`concrete-grade-${idx}`} value={item.concrete_grade}
                    onChange={e => {
                      const grade = CONCRETE_GRADES.find(g => g.name === e.target.value);
                      const items = [...form.items];
                      items[idx] = { ...items[idx], concrete_grade: e.target.value, concrete_rate: grade ? grade.rate : items[idx].concrete_rate };
                      setForm(prev => ({ ...prev, items }));
                      setNeedsRecalc(true);
                    }}
                    className="w-full px-2 py-1.5 text-xs border border-teal-200 rounded-md bg-white">
                    <option value="">Select Grade</option>
                    {CONCRETE_GRADES.map(g => <option key={g.name} value={g.name}>{g.name} (₹{g.rate.toLocaleString()}/m³)</option>)}
                  </select>
                </div>
                <div className="col-span-1">
                  <label className="block text-[9px] text-teal-600 mb-0.5">Rate/m³</label>
                  <input data-testid={`concrete-rate-${idx}`} type="number" value={item.concrete_rate} onChange={e => updateItem(idx, 'concrete_rate', e.target.value)} placeholder="5500" className="w-full px-2 py-1.5 text-xs border border-teal-200 rounded-md bg-white" />
                </div>
                <div className="col-span-1">
                  <label className="block text-[9px] text-teal-600 mb-0.5">Pits</label>
                  <input data-testid={`num-pits-${idx}`} type="number" value={item.num_pits} onChange={e => updateItem(idx, 'num_pits', e.target.value)} placeholder="2" className="w-full px-2 py-1.5 text-xs border border-teal-200 rounded-md bg-white" />
                </div>
                <div className="col-span-1">
                  <label className="block text-[9px] text-teal-600 mb-0.5">L (m)</label>
                  <input data-testid={`pit-length-${idx}`} type="number" step="0.1" value={item.pit_length_m} onChange={e => updateItem(idx, 'pit_length_m', e.target.value)} placeholder="0.6" className="w-full px-2 py-1.5 text-xs border border-teal-200 rounded-md bg-white" />
                </div>
                <div className="col-span-1">
                  <label className="block text-[9px] text-teal-600 mb-0.5">W (m)</label>
                  <input data-testid={`pit-width-${idx}`} type="number" step="0.1" value={item.pit_width_m} onChange={e => updateItem(idx, 'pit_width_m', e.target.value)} placeholder="0.6" className="w-full px-2 py-1.5 text-xs border border-teal-200 rounded-md bg-white" />
                </div>
                <div className="col-span-1">
                  <label className="block text-[9px] text-teal-600 mb-0.5">D (m)</label>
                  <input data-testid={`pit-depth-${idx}`} type="number" step="0.1" value={item.pit_depth_m} onChange={e => updateItem(idx, 'pit_depth_m', e.target.value)} placeholder="1.2" className="w-full px-2 py-1.5 text-xs border border-teal-200 rounded-md bg-white" />
                </div>
                <div className="col-span-2">
                  {item.pit_length_m > 0 && item.pit_width_m > 0 && item.pit_depth_m > 0 && item.num_pits > 0 && (
                    <div className="text-[10px] bg-teal-100 text-teal-800 px-2 py-1.5 rounded-md font-medium">
                      Vol: {(Number(item.pit_length_m) * Number(item.pit_width_m) * Number(item.pit_depth_m) * Number(item.num_pits)).toFixed(3)} m³
                      {item.concrete_rate > 0 && <span className="ml-1">= ₹{Math.round(Number(item.pit_length_m) * Number(item.pit_width_m) * Number(item.pit_depth_m) * Number(item.num_pits) * Number(item.concrete_rate)).toLocaleString()}</span>}
                    </div>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-12 gap-2 items-end">
                <div className="col-span-2">
                  <label className="block text-[9px] text-teal-600 mb-0.5">Rebar (kg) <span className="text-[8px] text-gray-400">optional</span></label>
                  <input data-testid={`rebar-kg-${idx}`} type="number" value={item.rebar_kg} onChange={e => updateItem(idx, 'rebar_kg', e.target.value)} placeholder="0" className="w-full px-2 py-1.5 text-xs border border-teal-200 rounded-md bg-white" />
                </div>
                <div className="col-span-1">
                  <label className="block text-[9px] text-teal-600 mb-0.5">₹/kg</label>
                  <input data-testid={`rebar-rate-${idx}`} type="number" value={item.rebar_rate} onChange={e => updateItem(idx, 'rebar_rate', e.target.value)} placeholder="75" className="w-full px-2 py-1.5 text-xs border border-teal-200 rounded-md bg-white" />
                </div>
                {Number(item.rebar_kg) > 0 && <div className="col-span-2"><div className="text-[10px] bg-teal-100 text-teal-800 px-2 py-1.5 rounded-md font-medium">Rebar: ₹{Math.round(Number(item.rebar_kg) * Number(item.rebar_rate || DEFAULT_REBAR_RATE)).toLocaleString()}</div></div>}
              </div>
            </>
          )}
        </div>
      )}

      {/* Calculated result row */}
      {itemCalc && (
        <div className="mt-1 pt-2 border-t border-gray-100 space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex flex-wrap gap-2">
              <span className="text-[10px] bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full font-medium">{itemCalc.area_sqft} sqft</span>
              {showInternalCosts && (
                <>
                  <span className="text-[10px] bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">₹{itemCalc.cost_per_sqft}/sqft</span>
                  <span className="text-[10px] bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">Facia: {itemCalc.facia_rate.toLocaleString()}{itemCalc.laminate_per_sqft > 0 ? ` (incl. lamination ₹${itemCalc.laminate_per_sqft}/sqft)` : ''}</span>
                  {itemCalc.post_rate > 0 && <span className="text-[10px] bg-orange-50 text-orange-600 px-2 py-0.5 rounded-full">Post: {itemCalc.post_rate.toLocaleString()} ({itemCalc.post_weight_kg}kg)</span>}
                  {itemCalc.frame_rate > 0 && <span className="text-[10px] bg-purple-50 text-purple-600 px-2 py-0.5 rounded-full">Frame: {itemCalc.frame_rate.toLocaleString()} ({itemCalc.frame_weight_kg}kg)</span>}
                  {itemCalc.installation_rate > 0 && <span className="text-[10px] bg-green-50 text-green-600 px-2 py-0.5 rounded-full">Install: {itemCalc.installation_rate.toLocaleString()}</span>}
                  {itemCalc.foundation_cost > 0 && <span className="text-[10px] bg-teal-50 text-teal-600 px-2 py-0.5 rounded-full">Foundation: {itemCalc.foundation_cost.toLocaleString()}</span>}
                </>
              )}
              <span className="text-[10px] bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full font-bold">Per Board: {itemCalc.final_per_board.toLocaleString()}</span>
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: '#24549c20', color: '#24549c' }}>Total: {itemCalc.total_amount.toLocaleString()}</span>
            </div>
          </div>
          {/* Pricing Mode Selector */}
          <div className="flex items-center gap-3 bg-amber-50/50 border border-amber-100 rounded-lg px-3 py-2 flex-wrap">
            <span className="text-[10px] font-semibold text-gray-600">Quote as:</span>
            <div className="flex rounded-md overflow-hidden border border-gray-200">
              <button data-testid={`pricing-mode-sqft-${idx}`} onClick={() => updateItem(idx, 'pricing_mode', 'sqft')}
                className={`px-3 py-1 text-[10px] font-semibold transition-all ${(item.pricing_mode !== 'per_pcs' && item.pricing_mode !== 'sqm') ? 'bg-blue-600 text-white' : 'bg-white text-gray-500 hover:bg-gray-50'}`}>Per Sq.ft</button>
              {item.shape === 'Rectangular' && (
                <button data-testid={`pricing-mode-sqm-${idx}`} onClick={() => {
                  const items = [...form.items];
                  const areaSqm = computeAreaSqm({ shape: items[idx].shape, length_mm: items[idx].length_mm, height_mm: items[idx].height_mm });
                  items[idx] = { ...items[idx], pricing_mode: 'sqm', custom_sqm_rate: items[idx].custom_sqm_rate || (areaSqm > 0 ? Math.round(itemCalc.final_per_board / areaSqm) : 0) };
                  setForm(prev => ({ ...prev, items }));
                }}
                  className={`px-3 py-1 text-[10px] font-semibold transition-all border-l border-gray-200 ${item.pricing_mode === 'sqm' ? 'bg-blue-600 text-white' : 'bg-white text-gray-500 hover:bg-gray-50'}`}>Per Sq.m</button>
              )}
              <button data-testid={`pricing-mode-pcs-${idx}`}
                onClick={() => {
                  const items = [...form.items];
                  items[idx] = { ...items[idx], pricing_mode: 'per_pcs', custom_per_pcs_rate: items[idx].custom_per_pcs_rate || Math.round(itemCalc.final_per_board) };
                  setForm(prev => ({ ...prev, items }));
                }}
                className={`px-3 py-1 text-[10px] font-semibold transition-all border-l border-gray-200 ${item.pricing_mode === 'per_pcs' ? 'bg-blue-600 text-white' : 'bg-white text-gray-500 hover:bg-gray-50'}`}>Per Pcs</button>
            </div>
            {item.pricing_mode === 'per_pcs' && (
              <div className="flex items-center gap-2">
                <label className="text-[10px] text-gray-500">Rate/Pc:</label>
                <input data-testid={`custom-pcs-rate-${idx}`} type="number" value={item.custom_per_pcs_rate} onChange={e => updateItem(idx, 'custom_per_pcs_rate', e.target.value)} className="w-24 px-2 py-1 text-xs border border-amber-200 rounded-md bg-white focus:ring-1 focus:ring-amber-300" placeholder={Math.round(itemCalc.final_per_board).toString()} />
                <span className="text-[10px] text-amber-700 font-semibold">= ₹{(Number(item.custom_per_pcs_rate || itemCalc.final_per_board) * item.qty).toLocaleString()}</span>
              </div>
            )}
            {item.pricing_mode === 'sqm' && item.shape === 'Rectangular' && (() => {
              const areaSqm = computeAreaSqm({ shape: item.shape, length_mm: item.length_mm, height_mm: item.height_mm });
              const totalSqm = Math.round(areaSqm * (Number(item.qty) || 0) * 1000) / 1000;
              const lineAmount = (netAmount != null) ? Number(netAmount) : itemCalc.final_per_board * (Number(item.qty) || 0);
              const defaultRate = totalSqm > 0 ? Math.round(lineAmount / totalSqm) : 0;
              const effRate = Number(item.custom_sqm_rate) || defaultRate;
              const effTotal = Math.round(effRate * totalSqm);
              return (
                <div className="flex items-center gap-2" data-testid={`sqm-info-${idx}`}>
                  <label className="text-[10px] text-gray-500">Rate/Sq.m:</label>
                  <input data-testid={`custom-sqm-rate-${idx}`} type="number" value={item.custom_sqm_rate ?? ''} onChange={e => updateItem(idx, 'custom_sqm_rate', e.target.value)} className="w-24 px-2 py-1 text-xs border border-blue-200 rounded-md bg-white focus:ring-1 focus:ring-blue-300" placeholder={defaultRate.toString()} />
                  <span className="text-[10px] bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full font-semibold">{totalSqm} SQM</span>
                  <span className="text-[10px] text-blue-700 font-semibold">= ₹{effTotal.toLocaleString()}</span>
                </div>
              );
            })()}
          </div>
          {/* Per-item discount */}
          <div className="flex items-center gap-2 bg-rose-50/50 border border-rose-100 rounded-lg px-3 py-2">
            <span className="text-[10px] font-semibold text-gray-600">Discount:</span>
            <select data-testid={`signage-item-disc-type-${idx}`} value={item.disc_type || 'amount'} onChange={e => updateItem(idx, 'disc_type', e.target.value)}
              className="px-1.5 py-1 text-[10px] border border-gray-200 rounded-md bg-white focus:ring-1 focus:ring-rose-300">
              <option value="amount">₹</option>
              <option value="percent">%</option>
            </select>
            <input data-testid={`signage-item-disc-value-${idx}`} type="number" min="0" value={item.disc_value || ''} onChange={e => updateItem(idx, 'disc_value', e.target.value)}
              placeholder="0" className="w-24 px-2 py-1 text-xs border border-rose-200 rounded-md bg-white focus:ring-1 focus:ring-rose-300" />
            {Number(item.disc_value) > 0 && (() => {
              const isPcs = item.pricing_mode === 'per_pcs';
              const rate = isPcs ? Number(item.custom_per_pcs_rate || itemCalc.final_per_board) : itemCalc.final_per_board;
              const gross = Math.round(rate) * Number(item.qty || 1);
              const dv = Number(item.disc_value) || 0;
              const disc = Math.min((item.disc_type === 'percent') ? Math.round(gross * dv / 100) : Math.round(dv), gross);
              return <span className="text-[10px] text-rose-700 font-semibold">−₹{disc.toLocaleString()} → ₹{(gross - disc).toLocaleString()}</span>;
            })()}
          </div>
        </div>
      )}
    </div>
  );
};

export default SignageBoardItem;
